import { css, html, LitElement, nothing, type PropertyValues } from 'lit'
import { getPathPrefix } from './helpers/href'
import { customElement, property, state } from 'lit/decorators.js'
import { booleanAttributeSchema, positiveIntegerAttributeSchema } from './helpers/yup'

const FAST_SWIPE_THRESHOLD = 200

@customElement('zaptic-carousel')
export class Carousel extends LitElement {
    static styles = css`
        :host {
            display: flex;
            flex-direction: column;
            position: relative;
            /* center image if this component doesn't match the image aspect ratio */
            justify-content: center;
            align-items: center;

            width: 100%;
            height: 100%;
            max-width: inherit;
            min-width: 100%;
            max-height: inherit;
            min-height: 100%;

            --arrow-button-size: 32px;
            --arrow-button-color: rgba(255, 255, 255, 0.85);
        }

        .slides {
            display: flex;
            width: 100%;
            /* center image if this component doesn't match the image aspect ratio */
            justify-content: center;
            align-items: center;
            min-height: calc(var(--arrow-button-size) + 20px);
            height: 100%;
        }

        slot {
            display: flex;
            align-items: center;
            height: 100%;
            width: 100%;
            min-height: calc(var(--arrow-button-size) + 20px);
            position: relative;
            overflow-x: scroll;
            scroll-snap-type: x mandatory;
            scroll-behavior: smooth;
            overflow-y: hidden;
        }

        slot.disable-scroll {
            overflow: hidden;
        }

        slot::-webkit-scrollbar {
            display: none;
        }

        ::slotted(*) {
            height: auto;
            max-width: 100%;
            position: absolute;
            scroll-snap-align: center;
            width: 100%;
        }

        .arrows {
            display: flex;
            align-items: center;
            justify-content: space-between;
            position: absolute;
            height: 100%;
            width: 95%;
            pointer-events: none;
        }

        .arrows .arrow {
            height: var(--arrow-button-size);
            width: var(--arrow-button-size);
            border-radius: 50%;
            visibility: hidden;
            pointer-events: all;
        }

        .arrows .arrow.visible {
            visibility: visible;
            opacity: 0.7;
        }

        .arrows .arrow.visible:hover {
            opacity: 1;
            cursor: pointer;
        }

        .dots {
            padding: 5px;
            color: #b2c2db;
        }

        .arrow.left img {
            transform: rotate(180deg);
        }

        zaptic-dots-overview {
            margin: 7px 0;
        }
    `

    @property({ attribute: 'arrows', converter: (newValue) => booleanAttributeSchema.validateSync(newValue) })
    arrows: boolean = false

    @property({ attribute: 'swipe', converter: (newValue) => booleanAttributeSchema.validateSync(newValue) })
    swipe: boolean = false

    @property({ attribute: 'active', converter: (newValue) => positiveIntegerAttributeSchema.validateSync(newValue) })
    active: number = 0

    @state()
    private touchStartX: number = 0

    @state()
    private touchEndX: number = 0

    @state()
    private touchStartY: number = 0

    @state()
    private startSwipeTime: number = 0

    connectedCallback() {
        super.connectedCallback()
        this.setSwipe(this.swipe)
    }

    private setSwipe(enabled: boolean = true) {
        if (enabled) {
            this.addEventListener('touchstart', this.onTouchStart, false)
            this.addEventListener('touchend', this.onTouchEnd, false)
            this.addEventListener('touchmove', this.onTouchMove, false)
            return
        }

        this.removeEventListener('touchstart', this.onTouchStart)
        this.removeEventListener('touchend', this.onTouchEnd)
        this.removeEventListener('touchmove', this.onTouchMove)
    }

    private onTouchStart = (event: TouchEvent) => {
        this.touchStartX = event.changedTouches[0].screenX
        this.touchStartY = event.changedTouches[0].screenY
        this.touchEndX = null

        this.startSwipeTime = Date.now()
    }

    private onTouchEnd = (event: TouchEvent) => {
        this.touchEndX = event.changedTouches[0].screenX
        this.handleGesture()
    }

    private onTouchMove = (event: TouchEvent) => {
        const touch = event.changedTouches[0]

        if (!this.detectSwipeIntent(touch)) {
            return
        }
        // Prevent scrolling.
        event.preventDefault()

        const slot = this.shadowRoot.querySelector('slot')

        // disable swipe left on last slide AND swipe right on first slide
        if (
            // last slide
            (this.active === slot?.assignedNodes().length - 1 && this.touchStartX > touch.screenX) ||
            // first slide
            (this.active === 0 && this.touchStartX < touch.screenX)
        ) {
            return
        }

        // limit slide change to one slide per swipe
        if (
            // going up
            (this.touchStartX > touch.screenX && this.touchStartX - touch.screenX > slot.clientWidth) ||
            // going down
            (this.touchStartX < touch.screenX && touch.screenX - this.touchStartX > slot.clientWidth)
        ) {
            return
        }

        const slotChildren = slot?.assignedElements()

        slotChildren?.forEach((child: HTMLElement, index) => {
            child.style.transform = `translate(${
                (index - this.active) * slot.clientWidth - (this.touchStartX - touch.screenX)
            }px, 0)`
        })
    }

    private detectSwipeIntent = (touch: Touch) => {
        const dx = this.touchStartX - touch.screenX
        const dy = this.touchStartY - touch.screenY

        if (Math.abs(dx) < Math.abs(dy)) {
            return
        }

        return true
    }

    private handleGesture = () => {
        const dswipe = Date.now() - this.startSwipeTime

        // swipe left
        if (this.touchEndX < this.touchStartX) {
            const slot = this.shadowRoot.querySelector('slot')
            // next slide threshold is set to 50% if slow swipe
            if (dswipe < FAST_SWIPE_THRESHOLD || this.touchStartX - this.touchEndX > slot.clientWidth / 2) {
                this.nextSlide()
                return
            }
            this.requestUpdate()
            return
        }

        // swipe right
        if (this.touchEndX > this.touchStartX) {
            const slot = this.shadowRoot.querySelector('slot')
            // next slide threshold is set to 50% if slow swipe
            if (dswipe < FAST_SWIPE_THRESHOLD || this.touchEndX - this.touchStartX > slot.clientWidth / 2) {
                this.prevSlide()
                return
            }
            this.requestUpdate()
            return
        }
    }

    private nextSlide = () => {
        const children = this.shadowRoot.querySelector('slot')?.assignedNodes()

        if (this.active >= children.length - 1) {
            return
        }

        this.active += 1

        this.setAttribute('active', `${this.active}`)
        this.requestUpdate()
    }

    private prevSlide = () => {
        if (this.active <= 0) {
            return
        }

        this.active -= 1

        this.setAttribute('active', `${this.active}`)
        this.requestUpdate()
    }

    private onSlotChange = () => {
        const slot = this.shadowRoot.querySelector('slot')
        const children = slot?.assignedNodes()

        if (!slot) {
            return
        }
        if (this.swipe) {
            slot.classList.remove('disable-scroll')
        } else {
            slot.classList.add('disable-scroll')
        }

        children?.forEach((child, index) => {
            if (!(child instanceof HTMLElement)) {
                return
            }

            if (index > 0) {
                child.style.transform = `translate(${index * 100}%, 0)`
            }
        })
        this.requestUpdate()
    }

    private supportsTouch() {
        return navigator.maxTouchPoints > 0
    }

    private handleClientDevice() {
        if (this.supportsTouch()) {
            return
        }

        this.arrows = true
        this.swipe = false
    }

    protected updated(changedProperties: PropertyValues<Carousel>) {
        if (changedProperties.has('swipe')) {
            this.setSwipe(this.swipe)
        }

        this.handleClientDevice()
    }

    render() {
        const pathPrefix = getPathPrefix()
        const slot = this.shadowRoot.querySelector('slot')
        const children = slot?.assignedNodes()
        const arrows = this.arrows
            ? html`
                  <div class="arrows">
                      <div class="arrow left ${this.active > 0 ? 'visible' : ''}" @click="${this.prevSlide}">
                          <img src="${pathPrefix}shared/images/chevron-right-grey.svg" />
                      </div>
                      <div
                          class="arrow right ${this.active < children?.length - 1 ? 'visible' : ''}"
                          @click="${this.nextSlide}"
                      >
                          <img src="${pathPrefix}shared/images/chevron-right-grey.svg" />
                      </div>
                  </div>
              `
            : nothing

        const dots =
            children?.length > 1
                ? html`<zaptic-dots-overview dots="${children?.length}" active="${this.active}"></zaptic-dots-overview>`
                : nothing

        const slotChildren = slot?.assignedElements()

        slotChildren?.forEach((child: HTMLElement, index) => {
            child.style.transform = `translate(${(index - this.active) * 100}%, 0)`
        })

        return html`
            <div class="slides">
                <slot @slotchange="${this.onSlotChange}"></slot>
                ${arrows}
            </div>
            ${dots}
        `
    }
}
